import React from "react";
import { Link } from "react-router-dom";
import { Download, ArrowRight } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PRODUCTS, MBESS_MODELS, FEATURES } from "@/lib/data";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { openExternal } from "@/lib/openExternal";

const CATALOG = "/downloads/tratou-catalog.pdf";

export default function Products() {
  return (
    <Layout>
      <section className="py-28 border-b border-white/10" data-testid="products-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Products</span>
          <h1 className="heading-massive mt-4">
            The Tratou <span className="text-tratou-green">MBESS Fleet.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-tratou-dim text-lg">
            Six Mobile BESS & Charging Station models — from 106 kWh up to 1.29 MWh — engineered to deliver
            clean kilowatt-hours wherever they are needed. Silent, instant, IoT-monitored.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact?intent=quote" className="btn-primary" data-testid="products-hero-quote">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={CATALOG} target="_blank" rel="noreferrer" onClick={(e) => openExternal(e, CATALOG)} className="btn-secondary" data-testid="products-hero-catalog">
              <Download className="w-4 h-4" /> Download Catalog
            </a>
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section className="py-24" data-testid="products-grid">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {PRODUCTS.map((p) => (
            <article key={p.name} className="bg-tratou-deep p-8 panel-hover group" data-testid={`product-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="aspect-[16/10] overflow-hidden border border-white/5 mb-6">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              </div>
              <h3 className="font-display text-2xl font-bold">{p.name}</h3>
              <p className="mt-2 text-tratou-dim">{p.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2 font-mono text-[11px] uppercase tracking-[0.15em]">
                {p.specs.map((s) => (
                  <span key={s} className="border border-white/10 px-3 py-1.5 text-tratou-green">{s}</span>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <Link to={`/contact?intent=quote&product=${encodeURIComponent(p.name)}`} className="btn-primary !py-3 !px-5" data-testid={`product-quote-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>Request Quote</Link>
                <a href={p.datasheet} target="_blank" rel="noreferrer" onClick={(e) => openExternal(e, p.datasheet)} className="btn-secondary !py-3 !px-5" data-testid={`product-datasheet-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <Download className="w-4 h-4" /> Datasheet
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* MBESS spec tabs */}
      <section className="py-24 border-t border-white/10" data-testid="products-specs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Technical Specifications</span>
          <h2 className="heading-section mt-4">Deep-dive into the <span className="text-tratou-green">MBESS series.</span></h2>

          <Tabs defaultValue={MBESS_MODELS[0].name} className="mt-12">
            <TabsList className="bg-tratou-deep border border-white/10 p-1 rounded-none">
              {MBESS_MODELS.map((m) => (
                <TabsTrigger key={m.name} value={m.name} className="font-mono text-xs uppercase tracking-[0.16em] rounded-none data-[state=active]:bg-tratou-green data-[state=active]:text-black" data-testid={`spec-tab-${m.name.toLowerCase()}`}>
                  {m.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {MBESS_MODELS.map((m) => (
              <TabsContent key={m.name} value={m.name} className="mt-8">
                <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                  <div className="bg-tratou-deep p-8">
                    <img src={m.image} alt={m.name} className="w-full aspect-[16/10] object-cover border border-white/5" />
                  </div>
                  <div className="bg-tratou-deep p-8">
                    <p className="eyebrow">{m.name}</p>
                    <p className="font-display text-5xl font-bold text-tratou-green mt-3">{m.capacity}</p>
                    <p className="text-tratou-dim mt-3">Use cases: {m.apps}</p>

                    <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                      <SpecCell k="Peak Power" v={m.peak} />
                      <SpecCell k="Continuous Output" v={m.continuous} />
                      <SpecCell k="Battery Voltage" v={m.voltage} />
                      <SpecCell k="Output AC" v={m.outputAC} />
                      <SpecCell k="Cell" v={m.cell} />
                      <SpecCell k="Weight" v={m.weight} />
                      <SpecCell k="Dimensions (W×D×H)" v={m.dims} />
                      <SpecCell k="Footprint" v={m.footprint} />
                      <SpecCell k="Protection" v="IP54 · C4 corrosion" />
                      <SpecCell k="Operating Temp" v="-10 → +50 °C · 0-95% RH" />
                      <SpecCell k="Cooling" v="Liquid cooled" />
                      <SpecCell k="Fire Protection" v="Gas bonding suppression" />
                      <SpecCell k="Charging Method" v="Grid AC / DC · Off-grid AC" />
                      <SpecCell k="Transport" v="Truckload · Trailer hitch" />
                    </div>

                    <div className="mt-6 flex gap-3">
                      <Link to={`/contact?intent=quote&product=${m.name}`} className="btn-primary !py-3" data-testid={`spec-quote-${m.name.toLowerCase()}`}>
                        Request Quote <ArrowRight className="w-4 h-4" />
                      </Link>
                      <a href={m.datasheet} target="_blank" rel="noreferrer" onClick={(e) => openExternal(e, m.datasheet)} className="btn-secondary !py-3" data-testid={`spec-datasheet-${m.name.toLowerCase()}`}>
                        <Download className="w-4 h-4" /> Datasheet
                      </a>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-24" data-testid="products-features">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Capabilities</span>
          <h2 className="heading-section mt-4">Every unit, fully equipped.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div key={label} className="bg-tratou-deep p-6 panel-hover">
                <Icon className="w-6 h-6 text-tratou-green mb-3" />
                <p className="font-display font-bold">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

function SpecCell({ k, v }) {
  return (
    <div className="bg-tratou-void p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-tratou-faint">{k}</p>
      <p className="mt-1 text-white text-sm">{v}</p>
    </div>
  );
}
